import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Package, MapPin, LogOut, Plus, Trash2, Heart, Gift, Eye, EyeOff, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatDA, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WILAYAS } from "@/lib/site";
import StatusTracker from "@/components/StatusTracker";
import ProductCard from "@/components/ProductCard";
import { useFavorites } from "@/context/FavoritesContext";
import { LoyaltyContent } from "@/pages/Loyalty";

function AuthForm() {
  const { login, register, verifyEmail, resendCode } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot | verify
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", identifier: "", email: "", password: "", referral_code: "" });
  const [pendingEmail, setPendingEmail] = useState("");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (mode === "login") {
        await login(f.identifier, f.password);
        toast.success("Connexion réussie");
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: f.email });
        toast.success(data.message || "Si un compte existe, un email vous a été envoyé.");
        setMode("login");
      } else if (mode === "verify") {
        await verifyEmail(pendingEmail, code);
        toast.success("Email vérifié — bienvenue chez L'olivier !");
      } else {
        await register({ first_name: f.first_name, last_name: f.last_name, email: f.email, password: f.password, referral_code: f.referral_code || null });
        setPendingEmail(f.email.trim().toLowerCase());
        setCode(""); setCooldown(60); setMode("verify");
        toast.success("Un code de vérification vous a été envoyé par email");
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const resend = async () => {
    setErr(""); setLoading(true);
    try {
      await resendCode(pendingEmail);
      setCooldown(60);
      toast.success("Un nouveau code vous a été envoyé");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const titles = { login: "Connexion", register: "Créer un compte", forgot: "Mot de passe oublié", verify: "Vérifiez votre email" };
  const subtitles = {
    login: "Accédez à votre espace L'olivier",
    register: "Rejoignez L'olivier en quelques secondes",
    forgot: "Réinitialisez votre mot de passe",
    verify: "Dernière étape avant la création de votre compte",
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12">
      <div className="bg-white rounded-3xl border border-slate-200/80 p-6 sm:p-8 shadow-sm">
        <h1 className="font-display font-extrabold text-2xl text-center mb-1">{titles[mode]}</h1>
        <p className="text-slate-500 text-sm text-center mb-6">{subtitles[mode]}</p>

        {mode !== "verify" && mode !== "forgot" && (
          <div className="flex bg-mint-50 rounded-full p-1 mb-6">
            <button onClick={() => setMode("login")} className={`flex-1 py-2 rounded-full text-sm font-semibold ${mode === "login" ? "bg-white shadow text-mint-700" : "text-slate-500"}`} data-testid="tab-login">Connexion</button>
            <button onClick={() => setMode("register")} className={`flex-1 py-2 rounded-full text-sm font-semibold ${mode === "register" ? "bg-white shadow text-mint-700" : "text-slate-500"}`} data-testid="tab-register">Inscription</button>
          </div>
        )}

        {err && <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-600 text-sm" data-testid="auth-error">{err}</div>}

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Prénom" value={f.first_name} onChange={set("first_name")} required testid="reg-firstname" />
                <Input label="Nom" value={f.last_name} onChange={set("last_name")} required testid="reg-lastname" />
              </div>
              <Input label="Email" type="email" value={f.email} onChange={set("email")} required testid="reg-email" placeholder="vous@exemple.com" />
              <Input label="Mot de passe" type="password" value={f.password} onChange={set("password")} required minLength={6} testid="reg-password" />
              <Input label="Code de parrainage (optionnel)" value={f.referral_code} onChange={set("referral_code")} testid="reg-referral" placeholder="OLIV-XXXXXX" />
              <p className="text-xs text-slate-400">Un code de vérification sera envoyé à cette adresse email. Le compte n'est créé qu'après validation du code.</p>
            </>
          ) : mode === "verify" ? (
            <>
              <p className="text-sm text-slate-500">Un code à 6 chiffres a été envoyé à <span className="font-semibold text-slate-700">{pendingEmail}</span>. Il est valable 15 minutes.</p>
              <Input label="Code de vérification" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required testid="verify-code" placeholder="000000" inputMode="numeric" autoComplete="one-time-code" />
            </>
          ) : mode === "forgot" ? (
            <>
              <p className="text-sm text-slate-500">Saisissez votre email : vous recevrez un lien par email pour créer un nouveau mot de passe.</p>
              <Input label="Email" type="email" value={f.email} onChange={set("email")} required testid="forgot-email" />
            </>
          ) : (
            <>
              <Input label="Email ou téléphone" value={f.identifier} onChange={set("identifier")} required testid="login-identifier" />
              <Input label="Mot de passe" type="password" value={f.password} onChange={set("password")} required testid="login-password" />
              <button type="button" onClick={() => { setErr(""); setMode("forgot"); }} data-testid="forgot-link" className="text-sm text-mint-700 hover:underline">Mot de passe oublié ?</button>
            </>
          )}
          <button type="submit" disabled={loading || (mode === "verify" && code.length !== 6)} data-testid="auth-submit"
            className="w-full py-3 rounded-full bg-mint-600 hover:bg-mint-700 text-white font-semibold shadow-lg shadow-mint-600/30 disabled:opacity-50">
            {loading ? "…" : mode === "login" ? "Se connecter" : mode === "forgot" ? "Envoyer le lien" : mode === "verify" ? "Vérifier et créer mon compte" : "Créer mon compte"}
          </button>
          {mode === "verify" && (
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={resend} disabled={loading || cooldown > 0} data-testid="resend-code"
                className="text-mint-700 font-semibold hover:underline disabled:opacity-50 disabled:no-underline">
                {cooldown > 0 ? `Renvoyer le code (${cooldown}s)` : "Renvoyer le code"}
              </button>
              <button type="button" onClick={() => { setMode("register"); setErr(""); }} data-testid="verify-back" className="text-slate-500 hover:underline">Modifier mes informations</button>
            </div>
          )}
          {mode === "forgot" && <button type="button" onClick={() => setMode("login")} className="w-full text-sm text-slate-500">Retour à la connexion</button>}
        </form>
      </div>
    </div>
  );
}

function Input({ label, testid, type, ...props }) {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  return (
    <div>
      <label className="block text-sm font-medium text-slate-600 mb-1.5">{label}</label>
      <div className="relative">
        <input {...props} type={isPassword ? (show ? "text" : "password") : type} data-testid={testid} className={`w-full px-4 py-2.5 rounded-xl border border-mint-200 outline-none focus:ring-2 focus:ring-mint-500 ${isPassword ? "pr-11" : ""}`} />
        {isPassword && (
          <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${testid}-toggle`} aria-label={show ? "Masquer" : "Afficher"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-mint-600">
            {show ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

function Dashboard() {
  const { user, logout } = useAuth();
  const { ids: favIds } = useFavorites();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [showAddr, setShowAddr] = useState(false);
  const [addr, setAddr] = useState({ label: "Domicile", full_name: "", phone: "", wilaya: "Alger", commune: "", street: "" });

  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/account/addresses").then((r) => setAddresses(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/favorites").then((r) => setFavorites(r.data)).catch(() => {});
  }, [favIds]);

  const [ecards, setEcards] = useState([]);
  useEffect(() => { api.get("/account/giftcards").then((r) => setEcards(r.data)).catch(() => {}); }, []);

  const [tier, setTier] = useState(null);
  useEffect(() => { api.get("/loyalty/me").then((r) => setTier(r.data?.enabled ? (r.data?.tier?.name || "__new__") : null)).catch(() => {}); }, []);
  const tierStyle = { BRONZE: "bg-amber-100 text-amber-800 border-amber-300", Silver: "bg-slate-100 text-slate-600 border-slate-300", Gold: "bg-yellow-100 text-yellow-700 border-yellow-400", __new__: "bg-slate-100 text-slate-500 border-slate-200" };

  const saveAddr = async () => {
    if (!addr.full_name || !addr.phone || !addr.street) { toast.error("Champs requis manquants"); return; }
    const { data } = await api.post("/account/addresses", addr);
    setAddresses(data); setShowAddr(false);
    setAddr({ label: "Domicile", full_name: "", phone: "", wilaya: "Alger", commune: "", street: "" });
    toast.success("Adresse ajoutée");
  };
  const delAddr = async (aid) => {
    if (!window.confirm("Supprimer cette adresse ?") || !window.confirm("Voulez-vous vraiment supprimer cette adresse ? Cette action est définitive.")) return;
    const { data } = await api.delete(`/account/addresses/${aid}`); setAddresses(data); toast.success("Adresse supprimée");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-mint-600 grid place-items-center text-white font-display font-bold text-xl">{user.first_name?.[0]}</div>
          <div>
            <h1 className="font-display font-extrabold text-xl sm:text-2xl">Bonjour, {user.first_name}</h1>
            <p className="text-slate-500 text-sm">{user.email || user.phone}</p>
            {tier && (
              <span data-testid="profile-tier-badge" className={`inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 rounded-full text-xs font-bold border ${tierStyle[tier] || tierStyle.__new__}`}>
                <Crown className="w-3.5 h-3.5" /> {tier === "__new__" ? "Nouveau membre" : `Statut ${tier}`}
              </span>
            )}
          </div>
        </div>
        <button onClick={logout} data-testid="logout-button" className="flex items-center gap-2 text-sm text-slate-500 hover:text-red-500"><LogOut className="w-4 h-4" /> Déconnexion</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setTab("orders")} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shrink-0 whitespace-nowrap ${tab === "orders" ? "bg-mint-600 text-white" : "bg-white border border-mint-200"}`} data-testid="tab-orders"><Package className="w-4 h-4" /> Mes commandes</button>
        <button onClick={() => setTab("addresses")} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shrink-0 whitespace-nowrap ${tab === "addresses" ? "bg-mint-600 text-white" : "bg-white border border-mint-200"}`} data-testid="tab-addresses"><MapPin className="w-4 h-4" /> Mes adresses</button>
        <button onClick={() => setTab("favorites")} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shrink-0 whitespace-nowrap ${tab === "favorites" ? "bg-mint-600 text-white" : "bg-white border border-mint-200"}`} data-testid="tab-favorites"><Heart className="w-4 h-4" /> Mes favoris</button>
        <button onClick={() => setTab("loyalty")} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shrink-0 whitespace-nowrap ${tab === "loyalty" ? "bg-mint-600 text-white" : "bg-white border border-mint-200"}`} data-testid="tab-loyalty"><Gift className="w-4 h-4" /> Fidélité</button>
      </div>

      {tab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 ? <p className="text-slate-400 text-center py-12">Aucune commande pour le moment.</p> :
            orders.map((o) => (
              <div key={o.id} className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid={`order-${o.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div><span className="text-xs text-slate-400 font-mono-label">#{o.id.slice(-8).toUpperCase()}</span><div className="text-sm text-slate-500">{new Date(o.created_at).toLocaleDateString("fr-FR")}</div></div>
                  <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">{o.status}</span>
                </div>
                <div className="mt-3 text-sm text-slate-600">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div>
                <div className="mt-4 pt-4 border-t border-mint-50"><StatusTracker status={o.status} /></div>
                <div className="mt-3 flex justify-between items-center"><span className="text-xs text-slate-400">{o.payment_method === "card" ? "Carte (démo)" : "À la livraison"}</span><span className="font-display font-bold text-mint-700">{formatDA(o.total)}</span></div>
              </div>
            ))}
        </div>
      )}

      {tab === "addresses" && (
        <div className="space-y-4">
          {addresses.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl border border-slate-200/80 p-5 flex justify-between items-start" data-testid={`address-${a.id}`}>
              <div>
                <div className="font-semibold">{a.label} · {a.full_name}</div>
                <div className="text-sm text-slate-500">{a.street}, {a.commune} {a.wilaya}</div>
                <div className="text-sm text-slate-400">{a.phone}</div>
              </div>
              <button onClick={() => delAddr(a.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {showAddr ? (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input label="Nom complet" value={addr.full_name} onChange={(e) => setAddr({ ...addr, full_name: e.target.value })} testid="addr-name" />
                <Input label="Téléphone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} testid="addr-phone" />
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Wilaya</label>
                  <select value={addr.wilaya} onChange={(e) => setAddr({ ...addr, wilaya: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-mint-200 bg-white">
                    {WILAYAS.map((w) => <option key={w}>{w}</option>)}
                  </select>
                </div>
                <Input label="Commune" value={addr.commune} onChange={(e) => setAddr({ ...addr, commune: e.target.value })} testid="addr-commune" />
              </div>
              <Input label="Adresse" value={addr.street} onChange={(e) => setAddr({ ...addr, street: e.target.value })} testid="addr-street" />
              <div className="flex gap-2">
                <button onClick={saveAddr} data-testid="addr-save" className="px-5 py-2.5 rounded-full bg-mint-600 text-white font-semibold text-sm">Enregistrer</button>
                <button onClick={() => setShowAddr(false)} className="px-5 py-2.5 rounded-full border border-mint-200 font-semibold text-sm">Annuler</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAddr(true)} data-testid="add-address-button" className="flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-mint-200 font-semibold text-sm hover:border-mint-400"><Plus className="w-4 h-4" /> Ajouter une adresse</button>
          )}
        </div>
      )}

      {tab === "favorites" && (
        <div data-testid="favorites-panel">
          {favorites.length === 0 ? (
            <p className="text-slate-400 text-center py-12">Vous n'avez pas encore de favoris. Cliquez sur le cœur d'un produit pour l'ajouter.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {favorites.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      )}

      {tab === "loyalty" && <div data-testid="loyalty-panel"><LoyaltyContent /></div>}

      {ecards.length > 0 && (
        <div className="mt-10" data-testid="my-ecards">
          <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Gift className="w-5 h-5 text-mint-600" /> Mes cartes cadeaux</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {ecards.map((c) => (
              <div key={c.id} className="rounded-2xl bg-gradient-to-br from-mint-600 to-mint-800 text-white p-5" data-testid={`ecard-${c.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-xs text-mint-100">Solde restant</div>
                    <div className="font-display font-extrabold text-2xl">{formatDA(c.balance)}</div>
                    <div className="text-xs text-mint-100 mt-0.5">sur {formatDA(c.amount)}</div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/20">{c.status === "active" ? "Active" : c.status === "scheduled" ? "Programmée" : c.status === "pending" ? "En attente" : c.status === "depleted" ? "Épuisée" : c.status}</span>
                </div>
                <div className="mt-3 font-mono font-bold tracking-wider bg-white/15 rounded-lg px-3 py-2 text-center">{c.code}</div>
                {c.message && <p className="text-xs text-mint-100 mt-2 italic">« {c.message} »</p>}
                <button onClick={() => window.print()} data-testid={`ecard-print-${c.id}`} className="mt-3 w-full py-2 rounded-full bg-white/20 hover:bg-white/30 text-sm font-semibold">Imprimer</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Account() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user && user.role === "admin") navigate("/admin"); }, [user]);
  if (loading) return <div className="py-24 text-center text-slate-400">Chargement…</div>;
  return user ? <Dashboard /> : <AuthForm />;
}
